import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Logo from '@z-components/atoms/Logo';
import GoogleMap from 'modules/common/components/GoogleMap/MapContainer';
import { SUPPORT_FORM_JA_HREF } from 'modules/static-pages/constants';

const AboutUsContent: FC = () => {
  const { t } = useTranslation('about');

  return (
    <>
      <header className="jumbotron">
        <section className="mission">
          <div className="about-page-logo">
            <Logo variant="white" width="100%" height="58px" />
          </div>
          <div className="about-page-title">
            {Array.from({ length: 4 }, (_, index) => (
              <span key={index}>{t(`title.${index}`)}</span>
            ))}
          </div>
        </section>
      </header>

      <section className="intro">
        <div className="container intro">
          <h3 className="intro-title">{t('intro.title')}</h3>
          {Array.from({ length: 3 }, (_, index) => (
            <div className="row" key={index}>
              <p className="col">{t(`intro.paragraph${index}`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="button-container">
        <a
          className="z-btn z-btn-primary"
          href="https://sites.google.com/view/zehitomo-recruiting-jp"
          rel="noreferrer"
          role="button"
          target="_blank"
        >
          {t('recruitingCta')}
        </a>
      </section>

      <section className="info">
        <div className="container">
          <h3 className="about-company-title">{t('info.title')}</h3>

          <div className="field">
            <div className="info-col label">{t('info.table.name.title')}</div>
            <div className="info-col info-content">
              <div className="company-info-content">{t('info.table.name.description')}</div>
            </div>
          </div>

          <div className="field">
            <div className="info-col label">{t('info.table.representatives.title')}</div>
            <div className="info-col info-content">
              <div className="company-info-content">
                {t('info.table.representatives.description')}
              </div>
            </div>
          </div>

          <div className="field">
            <div className="info-col label">{t('info.table.url.title')}</div>
            <div className="info-col info-content">
              <div className="company-info-content">https://www.zehitomo.com</div>
            </div>
          </div>

          <div className="field">
            <div className="info-col label">{t('info.table.address.title')}</div>
            <div className="info-col info-content">
              <div className="company-info-content">{t('info.table.address.description')}</div>
            </div>
          </div>

          <div className="field">
            <div className="info-col label">{t('info.table.shareholders.title')}</div>
            <div className="info-col info-content">
              <div className="company-info-content">{t('info.table.shareholders.description')}</div>
            </div>
          </div>

          <div className="field">
            <div className="info-col label">{t('info.table.contact.title')}</div>
            <div className="info-col info-content">
              <div className="company-info-content">
                <Trans
                  components={{
                    form: (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a href={SUPPORT_FORM_JA_HREF} rel="noopener noreferrer" />
                    ),
                  }}
                  i18nKey="about:info.table.contact.description"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="access">
        <div className="container">
          <h3 className="access-title">{t('access.title')}</h3>
          <p>{t('access.direction')}</p>
          <GoogleMap
            center={{
              lat: 35.6755718,
              lng: 139.7619711,
            }}
            marker={{
              lat: 35.6755718,
              lng: 139.7619711,
            }}
            height="400px"
            icon={{
              scaledSize: { height: 50, width: 50 },
              url: '/static/images/zehitomo-logo-map-marker.svg',
            }}
            zoom={14}
          />
        </div>
      </section>

      <style jsx>
        {`
          .jumbotron {
            width: 100%;
            background-color: var(--primary-color);
            height: 330px;
          }
          @media screen and (max-width: 768px) {
            .jumbotron {
              height: 468px;
            }
          }
          section {
            margin: 0 auto;
            border: 0;
            padding: 2rem 0;
            max-width: 850px;
            color: #2f2f2f;
          }
          section h3.title {
            font-size: 1.375rem;
            font-weight: 700;
            line-height: 1.875rem;
            text-transform: uppercase;
            padding: 0.5rem 0;
            margin-bottom: 3rem;
          }
          section p {
            font-size: 14px;
            line-height: 30px;
            margin: 0 0 1.5rem;
          }
          .mission {
            height: 100%;
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
            flex-direction: column;
            padding: 0.5rem 2rem;
          }
          .about-page-logo {
            display: flex;
            width: 280px;
            margin: 20px auto;
          }
          .about-page-title {
            color: #fff;
            font-weight: bold;
            font-size: 48px;
            line-height: 72px;
            align-items: center;
          }
          .about-page-title > span {
            display: inline;
          }
          .intro-title {
            padding-top: 3rem;
            padding-bottom: 1.5rem;
            font-size: 36px;
            line-height: 54px;
            white-space: pre-line;
          }
          .button-container {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          @media screen and (min-width: 769px) {
            .button-container .z-btn {
              line-height: 2.143rem;
              height: 4.286rem;
              width: 25.857rem;
              font-size: 1.286rem;
            }
          }
          .button-container .z-btn {
            text-decoration: none;
            line-height: 1.857rem;
            height: 3.714rem;
            width: 22.5rem;
          }
          .info {
            padding-top: 3rem;
          }
          .info .about-company-title {
            font-size: 24px;
            margin-bottom: 1rem;
          }
          .info .field {
            border-bottom: 1px solid #c4c4c4;
            padding: 1.5rem 0;
          }
          .info .field:first-of-type {
            border-top: 1px solid #c4c4c4;
          }
          .info .field .label {
            font-size: 14px;
            margin-bottom: 0.3125rem;
            font-weight: 700;
            max-width: 130px;
            width: 35%;
            vertical-align: top;
          }
          .info .field .info-content {
            width: 65%;
          }
          .info .field .info-col {
            display: inline-block;
            padding-left: 10px;
            padding-right: 10px;
          }
          .info .field .company-info-content {
            white-space: break-spaces;
            font-size: 14px;
            line-height: 1.5rem;
            margin-bottom: 0.125rem;
          }
          .info .field .company-info-content :global(a) {
            color: var(--primary-color);
            display: inline-block;
            border-bottom: 1px solid var(--primary-color);
            margin-bottom: -1px;
          }
          .access {
            margin: 20px auto 4rem;
          }
          .access-title {
            font-size: 24px;
          }
          @media screen and (max-width: 768px) {
            .mission {
              height: 620px;
              justify-content: center;
            }
            .about-page-title {
              font-size: 48px;
              white-space: nowrap;
            }
          }
          @media screen and (max-width: 540px) {
            section {
              padding: 1rem 0.5rem 2rem;
            }
            section.about .col-sm-6:last-child {
              margin: 0 auto;
            }
            .mission {
              text-align: left;
              max-width: 768px;
              align-items: flex-start;
              height: 100%;
            }
            .about-page-logo {
              width: 210px;
              margin-left: 10px;
            }
            .about-page-logo > svg {
              width: 210px;
            }
            .about-page-title > span {
              display: block;
            }
            .intro-title {
              padding-top: 1.5rem;
              padding-bottom: 1rem;
              font-size: 24px;
              line-height: 36px;
            }
            .access {
              margin-top: 0px;
              margin-bottom: 2rem;
            }
          }
        `}
      </style>
    </>
  );
};

export default AboutUsContent;
